import { useEffect, useMemo, useRef, useState } from "react";
import { GlassCard, Button, StatusPill, useToast } from "@/shared/ui";
import { useAuthConfig, useDetect, useHumanizeStream } from "@/shared/api/hooks";
import { useSettings } from "@/shared/api/settings";
import { useI18n } from "@/shared/i18n";
import { filterEnglishOnly } from "@/shared/text";
import { copyToClipboard } from "@/shared/clipboard";
import { ApiError } from "@/shared/api/client";
import { DetectBadge, providerLabel } from "@/features/detect/DetectBadge";
import { detectCost } from "@/features/detect/pricing";
import "./humanize.css";

/** Current production candidate search permits at most three attempts. */
function roundsForStrength(s: number): number {
  return Math.max(1, Math.min(3, Math.round(1 + 5 * s)));
}

function visibleDraftRounds(rounds: Iterable<number>, selected?: number | null): number[] {
  return [...new Set([...rounds, ...(selected != null ? [selected] : [])])].sort((a, b) => a - b);
}

/** Progress header for the live stream: queue position, then round + tokens. */
function StreamProgress({
  round,
  total,
  chars,
  queuePosition,
  queueEta,
  segTotal,
}: {
  round: number;
  total: number;
  chars: number;
  queuePosition: number | null;
  queueEta: number | null;
  segTotal: number | null;
}) {
  const { t } = useI18n();
  const known = total > 0 && round > 0;
  const queued = queuePosition != null;
  const pct = known ? Math.min(100, Math.round((round / total) * 100)) : 8;
  const label = queued
    ? queuePosition > 0
      ? t("In queue — {n} ahead of you, about {s}s to start", {
          n: queuePosition,
          s: queueEta ?? "…",
        })
      : t("You're next — waiting for a free slot ({s}s est.)", { s: queueEta ?? "…" })
    : known
      ? segTotal != null
        ? t("Draft {round} / {total} · {m} parts in parallel · {chars} chars so far", {
            round,
            total,
            m: segTotal,
            chars,
          })
        : t("Draft {round} / {total} · {chars} chars so far", { round, total, chars })
      : t("Request sent, waiting for the first token…");
  return (
    <div className="hz-progress" style={{ marginBottom: "var(--s3)" }}>
      <div className="hz-progress__label" style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>
        {label}
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: "var(--surface-2, rgba(255,255,255,0.08))",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 999,
            background: "var(--accent)",
            transition: "width 0.5s ease",
          }}
        />
      </div>
    </div>
  );
}

function DraftSlot({
  round,
  text,
  cos,
  recommended,
  pending,
  detector,
  fidelityOk,
}: {
  round: number;
  text?: string;
  cos?: number | null;
  recommended: boolean;
  pending: boolean;
  /** Configured detector id; undefined hides the check button. */
  detector?: string;
  fidelityOk?: boolean | null;
}) {
  const toast = useToast();
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Rejected worker snapshots may contain the source as a fallback and a
  // zero-filled chunk score. Neither is a delivered rewrite or a real cosine.
  const rejected = fidelityOk === false;
  const shown = rejected ? undefined : text || undefined;
  // One detector call per draft; the badge sticks to the text it scored.
  const detect = useDetect();
  const scored = detect.data && detect.variables === shown ? detect.data : null;

  async function check() {
    if (!shown) return;
    try {
      await detect.mutateAsync(shown);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("Detection failed, please retry"));
    }
  }

  async function copy() {
    const payload = shown;
    if (!payload) return;
    try {
      await copyToClipboard(payload);
      setCopied(true);
      toast.success(t("Draft {n} copied", { n: round }));
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const node = bodyRef.current;
      const sel = window.getSelection();
      if (node && sel) {
        const range = document.createRange();
        range.selectNodeContents(node);
        sel.removeAllRanges();
        sel.addRange(range);
        toast.error(t("Copy was blocked by the browser — the text is selected, press ⌘C"));
        return;
      }
      toast.error(t("Copy failed — select the text and press ⌘C"));
    }
  }

  return (
    <div className={`hz-draft${recommended ? " hz-draft--best" : ""}`}>
      <div className="hz-draft__bar">
        <div className="hz-draft__title">
          {t("Draft {n}", { n: round })}
          {recommended ? <StatusPill tone="ok">{t("Best")}</StatusPill> : null}
          {fidelityOk === false && <StatusPill tone="warn">{t("Failed fidelity check")}</StatusPill>}
          {fidelityOk === true && cos != null ? (
            <span className="hz-draft__cos">cos {cos.toFixed(3)}</span>
          ) : null}
        </div>
        <div className="hz-draft__actions">
          {detector && shown && (
            <button
              className="copy-btn"
              type="button"
              disabled={!shown || pending || fidelityOk === false || detect.isPending}
              onClick={() => void check()}
              title={
                shown
                  ? t("Costs about {n} words", { n: detectCost(countWords(shown)) })
                  : undefined
              }
            >
              {detect.isPending
                ? t("Checking…")
                : t("Check {p}", { p: providerLabel(detector) })}
            </button>
          )}
          {shown && <button
            className={`copy-btn ${copied ? "copy-btn--done" : ""}`}
            type="button"
            disabled={!shown || fidelityOk === false}
            onClick={() => void copy()}
            aria-label={t("Copy draft {n}", { n: round })}
          >
            {copied ? t("Copied ✓") : t("Copy")}
          </button>}
        </div>
      </div>
      {scored && shown && <DetectBadge result={scored} text={shown} />}
      <div
        ref={bodyRef}
        className={`hz-draft__body${shown ? "" : " hz-draft__body--wait"}`}
      >
        {rejected
          ? t("This attempt has no verified rewrite. The original text remains in the input.")
          : shown ?? t("Draft {n} not generated", { n: round })}
      </div>
    </div>
  );
}

// English humanizer limits. WORDS is the headline metric (what users think in);
// the worker truncates input at ~1024 tokens ≈ ~700 English words, so 600 words
// fits comfortably with no silent truncation. CHARS is a secondary safety valve
// for pathologically dense input and must stay ≤ the backend's MAX_INPUT_CHARS.
const MAX_WORDS = 600;
const MAX_CHARS = 5000;

function countWords(s: string): number {
  const t = s.trim();
  return t ? t.split(/\s+/).length : 0;
}

interface HumanizePageProps {
  /** Navigate to the Account tab (sign up / sign in lives there). */
  onGoAccount?: () => void;
  /** Text handed over from the detector tab; `n` changes per hand-off. */
  seed?: { text: string; n: number } | null;
}

export function HumanizePage({ onGoAccount, seed }: HumanizePageProps) {
  const { hasKey, settings } = useSettings();
  const toast = useToast();
  const { t } = useI18n();
  const stream = useHumanizeStream();
  const result = stream.result;
  const [text, setText] = useState("");
  const detector = useAuthConfig().data?.detect_provider;
  // Seed the editor from the detector hand-off (also on first mount).
  useEffect(() => {
    if (seed) { setText(seed.text); setFiltered(filterEnglishOnly(seed.text).removed > 0); }
  }, [seed]);
  // Three attempts are a retry budget, not repeated polishing or a quality score.
  const [strength, setStrength] = useState(0.4);
  // HIP/vLLM always apply the basic fidelity gate.
  const verify = false;
  // Pre-flight validation errors; stream errors come from the hook.
  const [error, setError] = useState<string | null>(null);
  // Unsupported characters remain visible until the user edits them.
  const [filtered, setFiltered] = useState(false);
  const previousAuth = useRef({ apiBase: settings.apiBase, credential: settings.jwt || settings.apiKey });
  useEffect(() => {
    const credential = settings.jwt || settings.apiKey;
    const previous = previousAuth.current;
    if ((previous.credential && previous.credential !== credential) || previous.apiBase !== settings.apiBase) {
      setText("");
      setFiltered(false);
      setError(null);
    }
    previousAuth.current = { apiBase: settings.apiBase, credential };
  }, [settings.apiBase, settings.jwt, settings.apiKey]);
  // Transient "Copied ✓" feedback on the copy button.
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);
  // Stream errors are English server strings; t() maps known ones to Chinese.
  const shownError = error ?? (stream.error ? t(stream.error) : null);

  async function copyOutput() {
    if (!result?.output) return;
    try {
      await copyToClipboard(result.output);
      setCopied(true);
      toast.success(t("Result copied to clipboard"));
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("Copy failed — select the text manually"));
    }
  }

  const words = useMemo(() => countWords(text), [text]);
  const overWords = words > MAX_WORDS;
  const overChars = text.length > MAX_CHARS;
  const over = overWords || overChars;
  // Keep the button clickable so an empty/filtered box shows an error instead
  // of a dead click. Only block while a job is in flight or the text is over limit.
  const canRun = !over && !stream.isPending;

  // Validate without destroying names, quotations, or full-width numbers.
  function onChangeText(raw: string) {
    const { removed } = filterEnglishOnly(raw);
    setText(raw);
    setFiltered(removed > 0);
  }

  async function run() {
    setError(null);
    if (filtered) {
      setError(t("Unsupported characters found. Edit the source before continuing; your original text is preserved."));
      return;
    }
    if (!hasKey) {
      setError(t("Sign up or sign in first — every account gets free words each month."));
      return;
    }
    if (!text.trim()) {
      setError(
        t(
          "Paste some English text first.",
        ),
      );
      return;
    }
    await stream.start({ text, strength, verify });
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title" style={{ display: "flex", alignItems: "center", gap: "var(--s3)" }}>
          {t("Humanize Text")}
          <StatusPill tone="accent">English only</StatusPill>
        </h1>
        <p className="page__sub">
          {t(
            "Paste English text to rewrite. Basic fidelity checks run automatically; review the result for factual accuracy. Your original input is preserved.",
          )}
        </p>
      </div>

      <div className="hz-grid">
        {/* Input */}
        <GlassCard title={t("Source (English AI text)")}>
          <textarea
            className="textarea"
            placeholder={t("Paste the AI-generated English text to humanize…")}
            value={text}
            onChange={(e) => onChangeText(e.target.value)}
          />
          <div className="hz-counter" style={over ? { color: "var(--danger)" } : undefined}>
            {words} / {MAX_WORDS} words
            <span style={{ opacity: 0.6 }}> · {text.length} chars</span>
            {overWords && <span> — {t("over the {n}-word limit", { n: MAX_WORDS })}</span>}
            {!overWords && overChars && (
              <span> — {t("over the {n}-char limit", { n: MAX_CHARS })}</span>
            )}
          </div>
          {filtered && (
            <div className="hz-error" style={{ color: "var(--warning)" }}>
              <span aria-hidden>ⓘ</span>
              {t("Unsupported characters found. Edit the source before continuing; your original text is preserved.")}
            </div>
          )}

          <div className="hz-controls">
            <div className="hz-strength">
              <span className="field-label" style={{ margin: 0 }}>
                {t("Maximum attempts")}
              </span>
              <input
                type="range"
                min={0}
                max={0.4}
                step={0.2}
                value={strength}
                onChange={(e) => setStrength(parseFloat(e.target.value))}
              />
              <span className="hz-strength__val">
                {t("Up to {n} attempts", { n: roundsForStrength(strength) })}
              </span>
            </div>
          </div>
          <div className="hz-strength-hint">
            {t(
              "Stops when a complete rewrite passes basic checks. Extra attempts retry failed sections. Review important details before using the result.",
            )}
          </div>

          <div style={{ marginTop: "var(--s3)" }}>
            <Button onClick={run} loading={stream.isPending} disabled={!canRun} block>
              {stream.isPending ? t("Humanizing…") : t("Humanize Now")}
            </Button>
          </div>

          {shownError && (
            <div className="hz-error">
              <span aria-hidden>⚠</span>
              {shownError}
            </div>
          )}
          {!hasKey && (
            <div className="hz-signup">
              <div className="hz-signup__text">
                {t("New here? Sign up in a minute — 500 free words every month.")}
                <span className="hz-signup__alt">
                  {t("Already have an API key? Paste it via ⚙ in the top-right corner.")}
                </span>
              </div>
              <Button variant="ghost" onClick={onGoAccount}>
                {t("Sign up / Sign in")}
              </Button>
            </div>
          )}
        </GlassCard>

        {/* Output */}
        <GlassCard
          title={t("Humanized Result")}
          action={
            result && !stream.isPending ? (
              <div className="hz-out-actions">
                {result.request_id && (
                  <StatusPill tone="accent">id {result.request_id.slice(0, 8)}</StatusPill>
                )}
                <button
                  className={`copy-btn ${copied ? "copy-btn--done" : ""}`}
                  type="button"
                  onClick={copyOutput}
                  aria-label={t("Copy best result")}
                >
                  {copied
                    ? t("Copied ✓")
                    : t("Copy best (draft {n})", { n: result.round_used ?? 5 })}
                </button>
              </div>
            ) : undefined
          }
        >
          {stream.isPending || result || stream.drafts.size > 0 ? (
            <div className="hz-out hz-out--drafts">
              {stream.isPending ? (
                <>
                  <StreamProgress
                    round={stream.round}
                    total={stream.total}
                    chars={
                      stream.liveText.length +
                      stream.liveSegs.reduce((acc, sg) => acc + sg.length, 0)
                    }
                    queuePosition={stream.queuePosition}
                    queueEta={stream.queueEta}
                    segTotal={stream.segTotal}
                  />
                  {stream.liveSegs.length > 0 ? (
                    // Chunked-parallel run: every part streams simultaneously.
                    <div className="hz-live hz-live--multi" aria-live="polite">
                      {stream.liveSegs.map((sg, k) => (
                        <div key={k} className="hz-live__seg">
                          {sg || <span className="hz-live__wait">…</span>}
                          <span className="hz-live__caret" aria-hidden />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="hz-live" aria-live="polite">
                      {stream.liveText || (
                        <span className="hz-live__wait">{t("Waiting for the model…")}</span>
                      )}
                      <span className="hz-live__caret" aria-hidden />
                    </div>
                  )}
                </>
              ) : null}
              {(() => {
                const slots = visibleDraftRounds(stream.drafts.keys(), result?.round_used);
                return slots.map((round) => {
                  const live = stream.drafts.get(round);
                  return (
                    <DraftSlot
                      key={round}
                      round={round}
                      text={live?.text}
                      cos={live?.cos}
                      recommended={!stream.isPending && result?.fidelity_ok === true && result?.round_used === round}
                      fidelityOk={live?.fidelity_ok}
                      pending={stream.isPending}
                      detector={detector}
                    />
                  );
                });
              })()}
            </div>
          ) : (
            <div className="hz-out hz-out--empty">
              <span>
                {t(
                  "Your rewrite continues while you visit other tabs. Startup may take a few minutes; completed drafts appear here.",
                )}
              </span>
            </div>
          )}

          {result && (
            <div className="hz-meta fadein">
              {result.round_used != null && (
                <StatusPill tone="neutral">{t("Draft {n}", { n: result.round_used })}</StatusPill>
              )}
              {result.cos != null && (
                <StatusPill tone="neutral">
                  {t("semantic cos {v}", { v: result.cos.toFixed(3) })}
                </StatusPill>
              )}
              {result.fidelity_ok != null &&
                (result.fidelity_ok ? (
                  <StatusPill tone="ok" dot>
                    {t("Passed basic fidelity checks")}
                  </StatusPill>
                ) : (
                  <StatusPill tone="warn" dot>
                    {t("Fidelity uncertain · double-check numbers/entities")}
                  </StatusPill>
                ))}
            </div>
          )}
        </GlassCard>
      </div>
    </>
  );
}
